import React, { type ReactElement, type ReactNode, useCallback } from 'react';

import { animated, useTransition } from '@react-spring/web';

import styles from './BottomSheet.styles';

export type BottomSheetProps = {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    shouldCloseOnBackdropClick?: boolean;
};

const BottomSheet = ({
    isOpen,
    onClose,
    children,
    shouldCloseOnBackdropClick = true,
}: BottomSheetProps): ReactElement | null => {
    const backdropTransition = useTransition(isOpen, {
        config: { tension: 150, friction: 20 },
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });
    const panelTransition = useTransition(isOpen, {
        from: { transform: 'translateY(100%)' },
        enter: { transform: 'translateY(0%)' },
        leave: { transform: 'translateY(100%)' },
    });

    const handleBackdropClick = useCallback(
        (event: React.MouseEvent) => {
            if (event.target === event.currentTarget) {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <>
            {backdropTransition(
                (backdropStyle, item) =>
                    item && (
                        <animated.div
                            css={styles.backdrop}
                            style={backdropStyle}
                            onClick={shouldCloseOnBackdropClick ? handleBackdropClick : undefined}
                        >
                            {panelTransition(
                                (panelStyle, panelItem) =>
                                    panelItem && (
                                        <animated.div css={styles.panel} style={panelStyle}>
                                            {children}
                                        </animated.div>
                                    ),
                            )}
                        </animated.div>
                    ),
            )}
        </>
    );
};

export default BottomSheet;
